import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { appToday } from "../../lib/devDate";
import type { Category } from "../../lib/types";
import { currentStatisticsMonth, defaultStatisticsFilters, statisticsMonthFilters, statisticsPresetFilters } from "./dateRange";
import { statisticsFiltersFromUrl, statisticsFiltersToUrlParams, statisticsMonthFromUrl, statisticsViewFromUrl } from "./statisticsUrlState";
import type { StatisticsFilters, StatisticsPreset, StatisticsView } from "./types";
import type { MonthlyOperationType } from "./MonthlyOperationsPreview";

export function useStatisticsUrlState({
  asOfDate,
  categories,
}: {
  asOfDate: string | null;
  categories: Category[];
}) {
  const [, setSearchParams] = useSearchParams();
  const [initialUrlFilters] = useState(() => statisticsFiltersFromUrl(window.location.search));
  const [view, setView] = useState<StatisticsView>(() => statisticsViewFromUrl(window.location.search));
  const [month, setMonth] = useState(() => statisticsMonthFromUrl(window.location.search));
  const [filters, setFilters] = useState<StatisticsFilters>(() => {
    const initial = initialUrlFilters || statisticsMonthFilters(month, defaultStatisticsFilters());
    return view === "overview" ? { ...initial, categoryId: "" } : initial;
  });
  const [merchantInput, setMerchantInput] = useState(() => initialUrlFilters?.merchant || "");
  const [selectedParentCategory, setSelectedParentCategory] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("uncategorized") === "1" ? "uncategorized" : params.get("category") || "";
  });
  const [selectedChildCategories, setSelectedChildCategories] = useState(() => new URLSearchParams(window.location.search).getAll("subcategory"));
  const [otherCategoryActive, setOtherCategoryActive] = useState(() => new URLSearchParams(window.location.search).get("other") === "1");

  useEffect(() => {
    const id = setTimeout(() => {
      setFilters((current) => current.merchant === merchantInput.trim() ? current : { ...current, merchant: merchantInput.trim() });
    }, 400);
    return () => clearTimeout(id);
  }, [merchantInput]);

  useEffect(() => {
    const urlFilters = view === "overview" ? { ...filters, categoryId: "" } : filters;
    const params = statisticsFiltersToUrlParams(urlFilters, view, month);
    if (view === "overview" && selectedParentCategory === "uncategorized") params.set("uncategorized", "1");
    else if (view === "overview" && selectedParentCategory) params.set("category", selectedParentCategory);
    if (view === "overview") selectedChildCategories.forEach((categoryId) => params.append("subcategory", categoryId));
    if (view === "overview" && otherCategoryActive) params.set("other", "1");
    setSearchParams(params, { replace: true });
  }, [filters, month, otherCategoryActive, selectedChildCategories, selectedParentCategory, setSearchParams, view]);

  useEffect(() => {
    if (initialUrlFilters) return;
    const currentMonth = currentStatisticsMonth(appToday());
    setMonth(currentMonth);
    setFilters((current) => statisticsMonthFilters(currentMonth, current, appToday()));
  }, [asOfDate, initialUrlFilters]);

  useEffect(() => {
    if (!selectedParentCategory || selectedParentCategory === "uncategorized" || selectedChildCategories.length || !categories.length) return;
    const selected = categories.find((category) => category.id === selectedParentCategory);
    if (!selected?.parent_id) return;
    setSelectedParentCategory(selected.parent_id);
    setSelectedChildCategories([selected.id]);
  }, [categories, selectedChildCategories.length, selectedParentCategory]);

  const changeMonth = (nextMonth: string) => {
    setMonth(nextMonth);
    setFilters((current) => statisticsMonthFilters(nextMonth, current, appToday()));
  };
  const changeView = (nextView: StatisticsView) => {
    setView(nextView);
    if (nextView === "overview") setFilters((current) => statisticsMonthFilters(month, current, appToday()));
    setSearchParams(statisticsFiltersToUrlParams(filters, nextView, month), { replace: false });
  };
  const setPreset = (preset: StatisticsPreset) => setFilters((current) => statisticsPresetFilters(preset, current, appToday()));
  const activePreset = useMemo<StatisticsPreset | null>(() => {
    const presets: StatisticsPreset[] = ["month", "lastMonth", "quarter", "year"];
    return presets.find((preset) => {
      const candidate = statisticsPresetFilters(preset, filters, appToday());
      return candidate.dateFrom === filters.dateFrom && candidate.dateTo === filters.dateTo;
    }) || null;
  }, [filters, asOfDate]);

  const clearFilters = () => {
    setMerchantInput("");
    if (view === "overview") {
      setSelectedParentCategory("");
      setSelectedChildCategories([]);
      setOtherCategoryActive(false);
      setFilters((current) => ({
        ...statisticsMonthFilters(month, current, appToday()),
        type: "",
        categoryId: "",
        userId: "",
        accountId: "",
        tagIds: [],
        merchant: "",
        compare: "previous",
      }));
    } else {
      setFilters(defaultStatisticsFilters(appToday()));
    }
  };
  const changeOverviewType = (type: MonthlyOperationType) => {
    setSelectedParentCategory("");
    setSelectedChildCategories([]);
    setOtherCategoryActive(false);
    setFilters((current) => ({ ...current, type, accountId: "" }));
  };
  const deleteOverviewFilter = (key: string) => {
    if (key === "type") {
      setSelectedParentCategory("");
      setSelectedChildCategories([]);
      setOtherCategoryActive(false);
      setFilters((current) => ({ ...current, type: "", accountId: "" }));
    } else if (key === "category" || key === "category-parent") {
      setSelectedParentCategory("");
      setSelectedChildCategories([]);
      setOtherCategoryActive(false);
      setFilters((current) => ({ ...current, accountId: "" }));
    } else if (key === "category-other") {
      setOtherCategoryActive(false);
      setSelectedChildCategories([]);
      setFilters((current) => ({ ...current, accountId: "" }));
    } else if (key.startsWith("category-child:")) {
      setSelectedChildCategories((current) => current.filter((categoryId) => categoryId !== key.slice("category-child:".length)));
      setFilters((current) => ({ ...current, accountId: "" }));
    } else if (key === "compare") setFilters((current) => ({ ...current, compare: "previous" }));
    else if (key === "user") setFilters((current) => ({ ...current, userId: "" }));
    else if (key === "account") setFilters((current) => ({ ...current, accountId: "" }));
    else if (key.startsWith("tag:")) setFilters((current) => ({ ...current, tagIds: current.tagIds.filter((tagId) => tagId !== key.slice(4)) }));
  };

  return {
    view,
    month,
    filters,
    setFilters,
    merchantInput,
    setMerchantInput,
    selectedParentCategory,
    setSelectedParentCategory,
    selectedChildCategories,
    setSelectedChildCategories,
    otherCategoryActive,
    setOtherCategoryActive,
    changeMonth,
    changeView,
    setPreset,
    activePreset,
    clearFilters,
    changeOverviewType,
    deleteOverviewFilter,
  };
}
